import type { StatusColor } from '@/components/base/status-cell'
import { fromPrecision, type PrecisionAmount } from '@/utils/currency'

/** Natural Monday-to-Sunday order for weekday sorting across promotion UIs. */
export const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const

export type DayOfWeekName = (typeof DAY_ORDER)[number]

/** Sort weekday values (e.g. 'MONDAY') naturally rather than alphabetically. */
export const sortDaysOfWeek = <T extends string>(days: T[]): T[] =>
	[...days].sort((a, b) => DAY_ORDER.indexOf(a as DayOfWeekName) - DAY_ORDER.indexOf(b as DayOfWeekName))

export type PromotionStatusFields = {
	enabled: boolean
	startDate?: number | null
	endDate?: number | null
}

type PromotionStatusCopy = {
	Inactive: () => string
	Expired: () => string
	Scheduled: () => string
	Active: () => string
}

export const getPromotionScheduleStatus = (
	promotion: PromotionStatusFields,
	copy: PromotionStatusCopy
): { text: string; color: StatusColor } => {
	if (!promotion.enabled) {
		return { text: copy.Inactive(), color: 'red' }
	}

	const now = Date.now()
	if (promotion.endDate && promotion.endDate < now) {
		return { text: copy.Expired(), color: 'grey' }
	}
	if (promotion.startDate && promotion.startDate > now) {
		return { text: copy.Scheduled(), color: 'blue' }
	}
	return { text: copy.Active(), color: 'green' }
}

/**
 * Native stores a fraction (0.20 = 20%) so checkout can multiply price by it.
 * Legacy BC rows stored percent points (20 = 20%); treat those as already in percent.
 *
 * Shift the fraction two decimal places on the integer value. `fromPrecision(amount) * 100`
 * is not safe to print: 20.2% is `{ value: 2020, precision: 4 }`, and `0.202 * 100` is
 * `20.200000000000003`.
 */
export const storedPercentageToPercentPoints = (amount: PrecisionAmount): number => {
	const n = fromPrecision(amount)
	if (n > 1) {
		return n
	}
	const scale = amount.precision - 2
	if (scale >= 0) {
		return amount.value / Math.pow(10, scale)
	}
	return amount.value * Math.pow(10, -scale)
}
